import { useState } from 'react'

import {
  ENGLISH_DOMAIN_CODE,
  ENGLISH_LEVELS,
  isValidTarget,
} from '@/domains/english/catalog/levels'
import {
  type Learner,
  type LearnerProfileInput,
  type LevelSelection,
} from '@/platform/learners/api'
import { INTEREST_OPTIONS } from '@/platform/learners/interests'
import {
  birthYearOptions,
  DAILY_MINUTES_DEFAULT,
  MAX_INTERESTS,
  NICKNAME_MAX_LENGTH,
  type ProfileField,
  STUDY_DAYS,
  validateProfile,
} from '@/platform/learners/profile'
import { toAppError } from '@/shared/errors/envelope'

/**
 * FR-02 learner profile: four short steps with tappable cards instead of a
 * long form. Used for both the first learner (onboarding) and later edits.
 */

export interface LearnerFormProps {
  readonly initial?: Learner
  readonly onSubmit: (
    profile: LearnerProfileInput,
    levels: LevelSelection,
  ) => Promise<void>
  readonly onCancel?: () => void
}

const DURATIONS = [10, 15, 20, 25, 30, 35, 40]
const STEPS = ['Bé là ai?', 'Trình độ tiếng Anh', 'Lịch học', 'Sở thích'] as const

interface Draft {
  nickname: string
  birthYear: number
  examDate: string
  dailyMinutes: number
  studyDays: number[]
  interests: string[]
  currentLevel: string
  targetLevel: string
}

function initialDraft(learner: Learner | undefined): Draft {
  const english = learner?.enrollments.find((e) => e.domain_code === ENGLISH_DOMAIN_CODE)
  return {
    nickname: learner?.nickname ?? '',
    birthYear: learner?.birth_year ?? 0,
    examDate: learner?.exam_date ?? '',
    dailyMinutes: learner?.daily_minutes ?? DAILY_MINUTES_DEFAULT,
    studyDays: learner?.study_days ?? [],
    interests: learner?.interests ?? [],
    currentLevel: english?.current_level ?? '',
    targetLevel: english?.target_level ?? '',
  }
}

function toProfile(d: Draft): LearnerProfileInput {
  return {
    nickname: d.nickname.trim(),
    birth_year: d.birthYear,
    exam_date: d.examDate === '' ? null : d.examDate,
    daily_minutes: d.dailyMinutes,
    study_days: [...d.studyDays].sort((a, b) => a - b),
    interests: d.interests,
  }
}

const STEP_FIELDS: readonly (readonly ProfileField[])[] = [
  ['nickname', 'birth_year'],
  ['exam_date'],
  ['daily_minutes', 'study_days'],
  ['interests'],
]

const chip = (selected: boolean) =>
  `rounded-2xl border-2 px-4 py-2 text-left transition-colors ${
    selected
      ? 'border-sky-700 bg-sky-50 dark:border-sky-400 dark:bg-sky-950/50'
      : 'border-slate-200 dark:border-slate-700'
  }`

export function LearnerForm({
  initial,
  onSubmit,
  onCancel,
}: LearnerFormProps): React.JSX.Element {
  const [step, setStep] = useState(0)
  const [draft, setDraft] = useState<Draft>(() => initialDraft(initial))
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({})
  const [busy, setBusy] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const update = (patch: Partial<Draft>) => {
    setDraft((d) => ({ ...d, ...patch }))
  }

  const stepErrors = (index: number): Partial<Record<string, string>> => {
    const all = validateProfile(toProfile(draft))
    const found: Partial<Record<string, string>> = {}
    for (const field of STEP_FIELDS[index] ?? []) {
      if (all[field] !== undefined) found[field] = all[field]
    }
    if (index === 1) {
      if (draft.currentLevel === '') found.currentLevel = 'Hãy chọn cấp độ hiện tại.'
      if (draft.targetLevel === '') found.targetLevel = 'Hãy chọn cấp độ mục tiêu.'
      else if (!isValidTarget(draft.currentLevel, draft.targetLevel)) {
        found.targetLevel = 'Mục tiêu không thấp hơn cấp độ hiện tại.'
      }
    }
    return found
  }

  const next = async () => {
    const found = stepErrors(step)
    setErrors(found)
    if (Object.keys(found).length > 0) return
    if (step < STEPS.length - 1) {
      setStep(step + 1)
      return
    }
    setBusy(true)
    setSubmitError(null)
    try {
      await onSubmit(toProfile(draft), {
        domainCode: ENGLISH_DOMAIN_CODE,
        currentLevel: draft.currentLevel,
        targetLevel: draft.targetLevel,
      })
    } catch (e) {
      setSubmitError(
        toAppError(e).code === 'INVALID_INPUT'
          ? 'Thông tin chưa hợp lệ. Hãy kiểm tra lại các bước.'
          : 'Không lưu được hồ sơ. Vui lòng thử lại.',
      )
      setBusy(false)
    }
  }

  const error = (key: string) =>
    errors[key] === undefined ? null : (
      <p role="alert" className="text-rose-700 dark:text-rose-400">
        {errors[key]}
      </p>
    )

  return (
    <main className="mx-auto flex min-h-dvh max-w-lg flex-col gap-6 px-5 py-8">
      <header>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {initial === undefined ? 'Hồ sơ của bé' : `Sửa hồ sơ của ${initial.nickname}`} ·
          Bước {step + 1}/{STEPS.length}
        </p>
        <div className="mt-2 flex gap-1" aria-hidden>
          {STEPS.map((s, i) => (
            <span
              key={s}
              className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-sky-700' : 'bg-slate-200 dark:bg-slate-800'}`}
            />
          ))}
        </div>
        <h1 className="mt-4 text-2xl font-semibold">{STEPS[step]}</h1>
      </header>

      {step === 0 && (
        <section className="flex flex-col gap-4">
          <label className="flex flex-col gap-1">
            <span className="font-medium">Tên gọi ở nhà</span>
            <input
              value={draft.nickname}
              maxLength={NICKNAME_MAX_LENGTH}
              autoComplete="off"
              onChange={(e) => {
                update({ nickname: e.target.value })
              }}
              className="rounded-xl border border-slate-300 px-4 text-lg dark:border-slate-700 dark:bg-slate-900"
              placeholder="Ví dụ: Bin"
            />
          </label>
          {error('nickname')}
          <label className="flex flex-col gap-1">
            <span className="font-medium">Năm sinh</span>
            <select
              value={draft.birthYear === 0 ? '' : String(draft.birthYear)}
              onChange={(e) => {
                update({ birthYear: Number(e.target.value) })
              }}
              className="rounded-xl border border-slate-300 px-4 text-lg dark:border-slate-700 dark:bg-slate-900"
            >
              <option value="">Chọn năm</option>
              {birthYearOptions().map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
          {error('birth_year')}
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Chỉ cần tên gọi và năm sinh. Ứng dụng không hỏi họ tên đầy đủ hay ngày sinh.
          </p>
        </section>
      )}

      {step === 1 && (
        <section className="flex flex-col gap-5">
          {(
            [
              ['currentLevel', 'Bé đang học cấp nào?'],
              ['targetLevel', 'Mục tiêu của bé là cấp nào?'],
            ] as const
          ).map(([field, question]) => (
            <div key={field} className="flex flex-col gap-2">
              <p className="font-medium">{question}</p>
              <div
                role="radiogroup"
                aria-label={question}
                className="grid grid-cols-3 gap-2"
              >
                {ENGLISH_LEVELS.map((level) => {
                  const selected = draft[field] === level.code
                  const disabled =
                    field === 'targetLevel' &&
                    draft.currentLevel !== '' &&
                    !isValidTarget(draft.currentLevel, level.code)
                  return (
                    <button
                      key={level.code}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      disabled={disabled}
                      onClick={() => {
                        update(
                          field === 'currentLevel' &&
                            draft.targetLevel !== '' &&
                            !isValidTarget(level.code, draft.targetLevel)
                            ? { currentLevel: level.code, targetLevel: '' }
                            : { [field]: level.code },
                        )
                      }}
                      className={`${chip(selected)} flex flex-col items-center py-3 text-center disabled:opacity-35`}
                    >
                      <span className="text-3xl" aria-hidden>
                        {level.icon}
                      </span>
                      <span className="font-semibold">{level.name}</span>
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        {level.cefr}
                      </span>
                    </button>
                  )
                })}
              </div>
              {error(field)}
            </div>
          ))}
          <label className="flex flex-col gap-1">
            <span className="font-medium">Ngày thi dự kiến (không bắt buộc)</span>
            <input
              type="date"
              value={draft.examDate}
              onChange={(e) => {
                update({ examDate: e.target.value })
              }}
              className="rounded-xl border border-slate-300 px-4 text-lg dark:border-slate-700 dark:bg-slate-900"
            />
          </label>
          {error('exam_date')}
        </section>
      )}

      {step === 2 && (
        <section className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <p className="font-medium">Mỗi ngày học bao lâu?</p>
            <div
              role="radiogroup"
              aria-label="Thời lượng mỗi ngày"
              className="flex flex-wrap gap-2"
            >
              {[...new Set([...DURATIONS, draft.dailyMinutes])]
                .sort((a, b) => a - b)
                .map((m) => (
                  <button
                    key={m}
                    type="button"
                    role="radio"
                    aria-checked={draft.dailyMinutes === m}
                    onClick={() => {
                      update({ dailyMinutes: m })
                    }}
                    className={chip(draft.dailyMinutes === m)}
                  >
                    {m} phút
                  </button>
                ))}
            </div>
            {error('daily_minutes')}
          </div>
          <div className="flex flex-col gap-2">
            <p className="font-medium">Học vào những ngày nào?</p>
            <div className="grid grid-cols-7 gap-1">
              {STUDY_DAYS.map(({ day, short, long }) => {
                const selected = draft.studyDays.includes(day)
                return (
                  <button
                    key={day}
                    type="button"
                    aria-pressed={selected}
                    aria-label={long}
                    onClick={() => {
                      update({
                        studyDays: selected
                          ? draft.studyDays.filter((d) => d !== day)
                          : [...draft.studyDays, day],
                      })
                    }}
                    className={`${chip(selected)} px-0 text-center font-semibold`}
                  >
                    {short}
                  </button>
                )
              })}
            </div>
            {error('study_days')}
          </div>
        </section>
      )}

      {step === 3 && (
        <section className="flex flex-col gap-3">
          <p className="text-slate-600 dark:text-slate-400">
            Chọn tối đa {MAX_INTERESTS} chủ đề bé thích ({draft.interests.length}/
            {MAX_INTERESTS}).
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {INTEREST_OPTIONS.map(({ code, label, icon }) => {
              const selected = draft.interests.includes(code)
              const full = !selected && draft.interests.length >= MAX_INTERESTS
              return (
                <button
                  key={code}
                  type="button"
                  aria-pressed={selected}
                  disabled={full}
                  onClick={() => {
                    update({
                      interests: selected
                        ? draft.interests.filter((i) => i !== code)
                        : [...draft.interests, code],
                    })
                  }}
                  className={`${chip(selected)} flex items-center gap-2 disabled:opacity-35`}
                >
                  <span className="text-2xl" aria-hidden>
                    {icon}
                  </span>
                  {label}
                </button>
              )
            })}
          </div>
          {error('interests')}
        </section>
      )}

      {submitError !== null && (
        <p role="alert" className="text-rose-700 dark:text-rose-400">
          {submitError}
        </p>
      )}

      <footer className="mt-auto flex gap-3">
        {step > 0 ? (
          <button
            type="button"
            className="h-12 flex-1 rounded-xl border border-slate-300 font-semibold dark:border-slate-700"
            disabled={busy}
            onClick={() => {
              setErrors({})
              setStep(step - 1)
            }}
          >
            Quay lại
          </button>
        ) : (
          onCancel !== undefined && (
            <button
              type="button"
              className="h-12 flex-1 rounded-xl border border-slate-300 font-semibold dark:border-slate-700"
              onClick={onCancel}
            >
              Hủy
            </button>
          )
        )}
        <button
          type="button"
          className="h-12 flex-1 rounded-xl bg-sky-700 text-lg font-semibold text-white disabled:opacity-50"
          disabled={busy}
          onClick={() => void next()}
        >
          {step < STEPS.length - 1 ? 'Tiếp tục' : busy ? 'Đang lưu…' : 'Lưu hồ sơ'}
        </button>
      </footer>
    </main>
  )
}
